import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CalendarClock, Download, Package, TriangleAlert, X } from 'lucide-react';
import { Ayuda, Badge, Button } from '@/components/ui';
import type { ClaveGlosario } from '@/lib/glosario';
import { CHART, tooltipStyle } from '@/components/charts/chartTheme';
import { EcuacionCompra } from './EcuacionCompra';
import { useFicha } from '@/lib/hooks';
import { exportarFicha } from '@/lib/exports';
import { inventoryStatus, orderStatus, recommendationStatus as recStatus, severity as sevMap } from '@/lib/status';
import { cn } from '@/lib/cn';
import { formatCompactCop, formatCop, formatNumber, formatShortDate } from '@shared/format';

/**
 * Ficha de un producto, en una ventana sobre la tabla.
 *
 * Existe porque para entender UN SKU había que recorrer cinco pantallas anotando: el
 * semáforo tiene el stock, el forecast la proyección, recomendaciones qué comprar, órdenes
 * lo que viene en camino y demanda insatisfecha lo que no se pudo entregar. Acá está todo
 * junto y se abre desde cualquiera de las dos tablas.
 *
 * Va sobre un `<dialog>` nativo con `showModal()`, no un div con z-index: así el fondo queda
 * inerte, Escape cierra y el foco no se escapa detrás — tres cosas que a mano se hacen mal.
 *
 * ⚠️ El centrado NO se deja en manos de `margin: auto`. La primera versión, que se apoyaba
 * en él, salía corrida: se midió `margin-top: 24px` con `margin-bottom: 0` — centrada a lo
 * ancho y pegada arriba a lo alto. El monorepo ya tiene anotado que un reset que pisa el
 * `margin: auto` del user-agent descentra un `<dialog>` modal; acá se cortó de raíz
 * centrando por **traslación**, que no depende de cómo se repartan los márgenes sobrantes.
 *
 * 📌 Honestidad sobre el diagnóstico: la causa exacta de aquellos 24px NO quedó demostrada.
 *    La sospecha era el `space-y-6` del AppShell metiéndole un `margin-top` al diálogo por
 *    ser hijo del contenido, pero al intentar reproducirlo —quitando el portal Y la
 *    traslación— el corrimiento no volvió a aparecer. Lo que sí está medido es el estado de
 *    hoy: 60px arriba y 60 abajo en un viewport de 1000, exacto, y `npm run e2e:ficha` lo
 *    comprueba en cada corrida comparando el centro de la caja contra el del viewport.
 *
 * Va además en un **portal al `<body>`**: un modal no ocupa lugar en el flujo, así que no
 * tiene por qué heredar el espaciado ni el contexto de apilamiento de la pantalla que lo
 * abre — y esta ficha se abre desde dos, con más candidatas.
 *
 * `vh` en un teléfono mide la ventana con la barra del navegador escondida, así que un alto
 * en `vh` deja contenido abajo del borde sin scroll: va en `dvh`.
 */
/**
 * Cuánto se tiene que haber alejado un punto de reorden FIJADO A MANO de lo que hoy daría la
 * cuenta para que valga avisar.
 *
 * 30 % y no 5 %: la demanda de un producto se mueve todo el tiempo y un aviso que salta por
 * cualquier cosa deja de leerse a la semana. Lo que se quiere marcar es el valor que quedó
 * VIEJO —cargado hace meses, con otra realidad— no el que difiere un poco.
 */
const DESVIO_AVISO = 0.3;

function desvio(f: { origenReorden: string; puntoReorden: number; puntoReordenCalculado: number }): boolean {
  if (f.origenReorden !== 'fijado' || f.puntoReordenCalculado <= 0) return false;
  return Math.abs(f.puntoReorden - f.puntoReordenCalculado) / f.puntoReordenCalculado > DESVIO_AVISO;
}

export function FichaProducto({ sku, onCerrar }: { sku: string | null; onCerrar: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const ficha = useFicha(sku);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (sku && !dlg.open) dlg.showModal();
    if (!sku && dlg.open) dlg.close();
  }, [sku]);

  const d = ficha.data;
  const p = d?.producto;
  const est = p ? inventoryStatus[p.status] : null;

  return createPortal(
    <dialog
      ref={ref}
      onClose={onCerrar}
      // Clic en el fondo = cerrar. El `::backdrop` no recibe eventos propios, así que se
      // mira si el clic cayó FUERA de la caja del diálogo.
      onClick={(e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const fuera =
          e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
        if (fuera) ref.current?.close();
      }}
      className={cn(
        // ⚠️ SIN `transform`, y esto no es cosmético: un ancestro con `transform` se vuelve
        // el bloque contenedor de sus descendientes `position: fixed`, así que el globo de
        // las ayudas que viven adentro de la ficha dejaba de posicionarse contra el viewport
        // y encima lo recortaba el `overflow-hidden` de acá. Se centra con `margin: auto`,
        // que estando en portal alcanza (medido: 60px arriba y 60 abajo en un viewport de
        // 1000). El `e2e:ficha` comprueba ese centrado en cada corrida.
        'm-auto',
        'w-[min(72rem,calc(100vw-2rem))] max-h-[min(88dvh,60rem)] overflow-hidden p-0',
        'rounded-2xl border border-hairline bg-card text-ink shadow-alta',
        'backdrop:bg-black/50 backdrop:backdrop-blur-sm',
      )}
      aria-label={p ? `Ficha de ${p.name}` : 'Ficha del producto'}
    >
      {/* Cabecera fija: el nombre no se pierde al bajar por la ficha. */}
      <div className="flex items-start justify-between gap-4 border-b border-hairline bg-card px-5 py-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {est && <Badge tone={est.tone}>{est.label}</Badge>}
            <span className="font-mono text-2xs text-ink-faint">{sku}</span>
          </div>
          <h2 className="mt-1 truncate text-lg font-bold tracking-tight text-ink-strong">
            {p?.name ?? (ficha.isLoading ? 'Cargando…' : 'Producto')}
          </h2>
          {p && (
            <p className="truncate text-xs text-ink-muted">
              {p.category} · {p.supplier}
            </p>
          )}
        </div>
        <button
          onClick={() => ref.current?.close()}
          aria-label="Cerrar"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-faint transition-[color,background-color] hover:bg-hover hover:text-ink-strong"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="max-h-[calc(min(88dvh,60rem)-5.5rem)] overflow-y-auto px-5 py-5">
        {ficha.isLoading && <p className="py-12 text-center text-sm text-ink-muted">Cargando la ficha…</p>}
        {ficha.isError && (
          <p className="py-12 text-center text-sm text-danger-text">
            No se pudo cargar: {(ficha.error as Error)?.message ?? 'error desconocido'}
          </p>
        )}

        {d && p && (
          <div className="space-y-6">
            {/* --- Por qué hay que comprar esto ------------------------------------
                Va PRIMERO y en cuerpo grande a pedido de Charly, y es coherente con lo que
                este proyecto ya tenía escrito de la tabla del forecast: «la explicación es
                el producto, no un extra — una orden que nadie puede justificar no se
                aprueba». Estaba abajo del todo, en 11,5px y gris: el lugar de una nota al
                pie para la frase que sostiene la decisión de compra. */}
            {d.forecast?.razon && (
              <section className="rounded-xl border-l-2 border-l-primary bg-card-2 py-4 pl-4 pr-4">
                <p className="section-label">Por qué</p>
                {/* `first-letter:uppercase`: el motor arma la frase en minúscula («vende casi
                    todos los días…»), que a 11px pasaba desapercibido y a este cuerpo salta a
                    la vista. Se arregla al PRESENTARLO — el dato que guarda la base no se
                    toca, porque de ahí lo leen también las alertas y las recomendaciones. */}
                <p className="mt-1.5 max-w-prosa text-[15px] leading-[1.65] text-ink first-letter:uppercase">
                  {d.forecast.razon}
                </p>
                <p className="mt-2.5 text-2xs text-ink-faint">
                  Vendió en {formatNumber(d.forecast.diasConVenta)} de los{' '}
                  {formatNumber(d.forecast.diasObservados)} días observados
                  {d.forecast.calculadoAt && <> · calculado el {formatShortDate(d.forecast.calculadoAt)}</>}
                </p>
              </section>
            )}

            {/* --- La foto de hoy ------------------------------------------------- */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Dato rotulo="Stock actual" valor={formatNumber(p.currentStock)} sufijo="uds" />
              <Dato
                rotulo="Cobertura"
                ayuda="cobertura"
                valor={`${p.coverageDays}d`}
                tono={p.coverageDays <= 0 ? 'danger' : undefined}
              />
              <Dato rotulo="Demanda/día"
                ayuda="demandaDia" valor={formatNumber(p.demandPerDay)} sufijo="uds" />
              <Dato
                rotulo="Tiempo de producción"
                ayuda="tiempoProduccion"
                valor={`${p.leadTimeDays}d`}
                // Un lead time por defecto no es un dato del proveedor: decirlo cambia
                // cuánta confianza merece la fecha de compra que sale de él.
                nota={d.forecast?.leadTimeSource === 'cargado' ? 'cargado' : 'por defecto'}
              />
              <Dato
                rotulo="Valor unitario"
                ayuda="valorUnitario"
                valor={p.unitCost > 0 ? formatCop(p.unitCost) : 'sin cargar'}
                tono={p.unitCost > 0 ? undefined : 'warning'}
              />
            </div>

            {/* --- Cómo viene vendiendo ------------------------------------------- */}
            <section>
              <h3 className="section-label">Venta de los últimos {d.serie.length} días</h3>
              {d.serie.length === 0 ? (
                <p className="mt-2 rounded-xl border border-hairline bg-card-2 px-4 py-6 text-center text-xs text-ink-muted">
                  Este SKU no registra venta en el período ingerido, así que no se le puede
                  calcular demanda ni cobertura. No es lo mismo que vender cero: es que no hay
                  con qué medirlo.
                </p>
              ) : (
                <div className="mt-2 h-48 rounded-xl border border-hairline bg-card-2 p-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={d.serie} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
                      <defs>
                        <linearGradient id="fichaFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.base} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={CHART.base} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tick={{ fill: CHART.axis, fontSize: 10, fontFamily: CHART.fontFamily }}
                        tickLine={false}
                        axisLine={false}
                        minTickGap={40}
                        tickFormatter={formatShortDate}
                      />
                      {/* Abreviado: "1.200" no entraba en el ancho del eje y Recharts lo
                          recortaba a "200", que no es un número más chico — es OTRO número.
                          Se vio en la captura, no en el código. */}
                      <YAxis
                        tick={{ fill: CHART.axis, fontSize: 10, fontFamily: CHART.fontFamily }}
                        tickLine={false}
                        axisLine={false}
                        width={44}
                        tickFormatter={(v: number) =>
                          v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1).replace('.', ',')}k` : String(v)
                        }
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        labelFormatter={formatShortDate}
                        formatter={(v: number) => [`${formatNumber(v)} uds`, 'Vendidas']}
                      />
                      <Area
                        type="monotone"
                        dataKey="units"
                        stroke={CHART.base}
                        strokeWidth={1.5}
                        fill="url(#fichaFill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>

            {/* --- Lo que dice el motor ------------------------------------------- */}
            {d.forecast ? (
              <section>
                <h3 className="section-label">Lo que calculó el motor</h3>
                <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {/* Cuando el número lo puso una persona hay que DECIRLO donde se lee, no
                      sólo en Configuración: cambia quién responde por él. */}
                  <Dato rotulo="Colchón de seguridad"
                ayuda="stockSeguridad" valor={formatNumber(d.forecast.stockSeguridad)} sufijo="uds"
                nota={d.forecast.origenColchon === 'fijado'
                  ? `fijado a mano: ${p.safetyPct}% de la espera`
                  : undefined} />
                  <Dato rotulo="Punto de reorden"
                ayuda="puntoReorden" valor={formatNumber(d.forecast.puntoReorden)} sufijo="uds"
                nota={d.forecast.origenReorden === 'fijado' ? 'fijado a mano' : undefined} />
                  <Dato
                    rotulo="Cuándo pedir"
                ayuda="cuandoPedir"
                    valor={d.forecast.diasHastaReorden <= 0 ? 'ahora' : `en ${d.forecast.diasHastaReorden}d`}
                    tono={d.forecast.diasHastaReorden <= 0 ? 'danger' : undefined}
                    nota={d.forecast.fechaLimite ? formatShortDate(d.forecast.fechaLimite) : undefined}
                  />
                  <Dato rotulo="Cuánto comprar" valor={formatNumber(d.forecast.cantidadSugerida)} sufijo="uds" />
                  {/* El error medido no se muestra: ver el comentario del Dashboard. En su
                      lugar va cuán seguido vende, que es lo que de verdad dice cuánta
                      confianza merece el número de al lado. */}
                  <Dato
                    rotulo="Vendió en"
                    valor={`${d.forecast.diasConVenta} d`}
                    nota={`de ${d.forecast.diasObservados} observados`}
                  />
                </div>
                {/* ⚠️ Un punto de reorden fijado a mano NO se actualiza solo: la demanda del
                    producto se mueve y ese número no. Sin este aviso el desvío es invisible —
                    y es la única contra que tiene la decisión de que el fijado mande. */}
                {desvio(d.forecast) && (
                  <p className="mt-3 max-w-prosa rounded-lg border-l-2 border-l-warning bg-card-2 px-3 py-2 text-xs leading-relaxed text-ink-muted">
                    <strong className="text-ink-strong">El punto de reorden fijado se alejó de la cuenta.</strong>{' '}
                    Está en {formatNumber(d.forecast.puntoReorden)} y con la demanda de hoy daría{' '}
                    {formatNumber(d.forecast.puntoReordenCalculado)}. Se sigue usando el que fijaste — revisalo en
                    Configuración → Configuración por Producto, o dejalo en blanco para que vuelva a calcularse solo.
                  </p>
                )}

                {/* La cuenta entera de cuánto comprar. Va sólo cuando hay algo que comprar:
                    con la compra en cero la ecuación es correcta y no le dice nada a nadie. */}
                {d.forecast.ecuacion && d.forecast.ecuacion.comprar > 0 && (
                  <EcuacionCompra
                    className="mt-4"
                    ecuacion={d.forecast.ecuacion}
                    origenColchon={d.forecast.origenColchon}
                    origenReorden={d.forecast.origenReorden}
                    safetyPct={p.safetyPct}
                  />
                )}
                {/* La explicación y la marca de cuándo se calculó ya van arriba de todo:
                    repetirlas acá sería decir dos veces lo mismo en la misma ventana. */}
              </section>
            ) : (
              <section>
                <h3 className="section-label">Lo que calculó el motor</h3>
                <p className="mt-2 max-w-prosa text-xs leading-relaxed text-ink-muted">
                  Todavía no tiene proyección. Pasa cuando el SKU no registra venta suficiente en
                  el período ingerido: sin serie no hay demanda que estimar, y forzar un número
                  sería inventarlo. Se recalcula solo con cada corrida del motor.
                </p>
              </section>
            )}

            {/* --- Qué hay que hacer con él --------------------------------------- */}
            {d.recomendacion && (
              <section className="rounded-xl border border-hairline bg-card-2 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <TriangleAlert className="h-4 w-4 text-warning-text" />
                    <h3 className="text-sm font-semibold text-ink-strong">
                      {d.recomendacion.status === 'pendiente'
                        ? 'Hay una recomendación de compra'
                        : 'Ya se decidió una compra'}
                    </h3>
                    <Badge tone={sevMap[d.recomendacion.severity].tone}>
                      {sevMap[d.recomendacion.severity].label}
                    </Badge>
                    {/* El ESTADO es la mitad de la información y faltaba: sin él, una
                        recomendación aprobada hace semanas se lee igual que una que espera
                        decisión. */}
                    {d.recomendacion.status !== 'pendiente' && (
                      <Badge tone={recStatus[d.recomendacion.status].tone}>
                        {recStatus[d.recomendacion.status].label}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-ink-muted">
                    Comprar <span className="cifra text-base">{formatNumber(d.recomendacion.qtyRecommended)}</span> uds
                    {d.recomendacion.costEstimate > 0 && <> · {formatCompactCop(d.recomendacion.costEstimate)} COP</>}
                  </p>
                </div>
                {/* ⚠️ Encontrado MIRANDO la ficha, no el código: una recomendación ya decidida
                    NO se reescribe cuando el motor recalcula —y está bien, una decisión tomada
                    no se pisa—, así que su cantidad puede quedar distinta de la cuenta de
                    arriba. En pantalla eso eran DOS NÚMEROS para la misma compra sin nada que
                    los explicara (medido: 16.704 contra 30.324 tras fijar el reorden a mano).
                    Se dice cuál es cuál en vez de esconder uno. */}
                {d.forecast?.ecuacion &&
                  d.forecast.ecuacion.comprar > 0 &&
                  d.forecast.ecuacion.comprar !== d.recomendacion.qtyRecommended && (
                    <p className="mt-2 max-w-prosa text-2xs leading-relaxed text-ink-faint">
                      Esa cantidad es la que se decidió en su momento y no cambia sola. Con los
                      números de hoy, la cuenta de arriba da{' '}
                      <strong className="text-ink">{formatNumber(d.forecast.ecuacion.comprar)} uds</strong>.
                    </p>
                  )}
              </section>
            )}

            {/* --- Lo que viene en camino ------------------------------------------ */}
            <div className="grid gap-6 lg:grid-cols-2">
              <section>
                <h3 className="section-label">Órdenes de compra</h3>
                {d.ordenes.length === 0 ? (
                  <p className="mt-2 text-xs text-ink-muted">Ninguna todavía para este producto.</p>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {d.ordenes.map((o) => {
                      const st = orderStatus[o.status as keyof typeof orderStatus];
                      return (
                        <li
                          key={o.oc_number}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hairline bg-card-2 px-3 py-2 text-xs"
                        >
                          <span className="flex items-center gap-2">
                            <Package className="h-3.5 w-3.5 text-ink-faint" />
                            <span className="font-mono text-primary-text">{o.oc_number}</span>
                          </span>
                          <span className="flex items-center gap-3">
                            <span className="text-ink">{formatNumber(o.quantity)} uds</span>
                            <span className="flex items-center gap-1 text-ink-muted">
                              <CalendarClock className="h-3 w-3" />
                              {formatShortDate(o.delivery_estimate)}
                            </span>
                            {st && <Badge tone={st.tone}>{st.label}</Badge>}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="section-label">Lo que no se pudo entregar</h3>
                {d.faltantes.length === 0 ? (
                  <p className="mt-2 text-xs text-ink-muted">Sin faltantes registrados en el período.</p>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {d.faltantes.map((f) => (
                      <li
                        key={f.date}
                        className="flex items-center justify-between gap-2 rounded-lg border border-hairline bg-card-2 px-3 py-2 text-xs"
                      >
                        <span className="text-ink-muted">{formatShortDate(f.date)}</span>
                        <span className="text-ink">
                          pidieron {formatNumber(f.requested)} · había {formatNumber(f.available)} ·{' '}
                          <span className="font-semibold text-danger-text">faltaron {formatNumber(f.gap)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between gap-2 border-t border-hairline bg-card px-5 py-3">
        {/* El reporte del producto: sirve para respaldar con el proveedor por qué se le está
            comprando esa cantidad. Sólo se ofrece con la ficha cargada — un botón que baja un
            archivo vacío es peor que no tenerlo. */}
        <Button
          variant="outline"
          size="md"
          icon={<Download className="h-4 w-4" />}
          disabled={!d}
          onClick={() => d && exportarFicha(d)}
        >
          Bajar el reporte
        </Button>
        <Button variant="subtle" size="md" onClick={() => ref.current?.close()}>
          Cerrar
        </Button>
      </div>
    </dialog>,
    document.body,
  );
}

/** Un dato de la ficha: rótulo arriba, cifra grande, y una nota chica si hace falta.
 *  Con `ayuda`, el rótulo explica el término — el mismo texto que en las tablas. */
function Dato({
  rotulo,
  valor,
  sufijo,
  nota,
  tono,
  ayuda,
}: {
  rotulo: string;
  valor: string;
  sufijo?: string;
  nota?: string;
  tono?: 'danger' | 'warning' | 'success';
  ayuda?: ClaveGlosario;
}) {
  return (
    <div className="rounded-xl border border-hairline bg-card-2 px-3 py-2.5">
      {ayuda ? (
        <Ayuda termino={ayuda} className="text-2xs uppercase tracking-wide">
          <span>{rotulo}</span>
        </Ayuda>
      ) : (
        <p className="text-2xs uppercase tracking-wide text-ink-faint">{rotulo}</p>
      )}
      <p
        className={cn(
          'cifra mt-0.5 text-xl',
          tono === 'danger' && 'text-danger-text',
          tono === 'warning' && 'text-warning-text',
          tono === 'success' && 'text-success-text',
        )}
      >
        {valor}
        {sufijo && <span className="ml-1 text-xs font-normal text-ink-muted">{sufijo}</span>}
      </p>
      {nota && <p className="mt-0.5 text-2xs text-ink-faint">{nota}</p>}
    </div>
  );
}
